import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';

const typeColors: Record<string, string> = {
  message: 'border-blue-400 bg-blue-50',
  input: 'border-amber-400 bg-amber-50',
  condition: 'border-violet-400 bg-violet-50',
  action: 'border-emerald-400 bg-emerald-50',
  ai: 'border-cyan-400 bg-cyan-50',
  dynamic_list: 'border-slate-400 bg-slate-50',
  subflow: 'border-fuchsia-400 bg-fuchsia-50',
  legacy_wrapper: 'border-gray-400 bg-gray-50',
  start: 'border-green-400 bg-green-50',
  end: 'border-rose-400 bg-rose-50',
  agent: 'border-indigo-400 bg-indigo-50',
  tool: 'border-teal-400 bg-teal-50',
  llm: 'border-sky-400 bg-sky-50',
  rag: 'border-orange-400 bg-orange-50',
};

export const FlowNodeComponent = memo(function FlowNodeComponent({
  data,
  selected,
}: NodeProps) {
  const label = (data?.label as string) ?? 'Node';
  const backendType = (data?.backendType as string) ?? 'default';
  const preview = data?.preview as string | undefined;
  const style = typeColors[backendType] ?? 'border-gray-300 bg-gray-50';
  const entrySlots = Array.isArray(data?.entrySlots)
    ? (data.entrySlots as unknown[]).filter((s): s is string => typeof s === 'string')
    : ['in'];
  const outputSlots = Array.isArray(data?.outputSlots)
    ? (data.outputSlots as unknown[]).filter((s): s is string => typeof s === 'string')
    : ['out'];

  return (
    <div
      className={`px-3 py-2 rounded-lg border-2 min-w-[120px] max-w-[200px] shadow-sm ${style} ${
        selected ? 'ring-2 ring-blue-400' : ''
      }`}
    >
      {entrySlots.length > 0 &&
        entrySlots.map((slot, idx) => (
          <Handle
            key={`in-${slot}`}
            id={slot}
            type="target"
            position={Position.Top}
            style={{ left: `${((idx + 1) / (entrySlots.length + 1)) * 100}%` }}
            className="!w-2 !h-2 !bg-gray-500"
            title={`in:${slot}`}
          />
        ))}
      <div className="text-sm font-medium text-gray-800">{label}</div>
      {preview ? (
        <div className="text-xs text-gray-500 truncate mt-0.5" title={preview}>{preview}</div>
      ) : (
        <div className="text-xs text-gray-400 truncate">{backendType}</div>
      )}
      {outputSlots.length > 0 &&
        outputSlots.map((slot, idx) => (
          <Handle
            key={`out-${slot}`}
            id={slot}
            type="source"
            position={Position.Bottom}
            style={{ left: `${((idx + 1) / (outputSlots.length + 1)) * 100}%` }}
            className="!w-2 !h-2 !bg-gray-500"
            title={`out:${slot}`}
          />
        ))}
    </div>
  );
});
